import React from 'react';
import Button from 'components/Button';
import { extractConductorName } from '../../../utils/Viewer';

const COPY = {
  new: {
    icon:     '✏️',
    title:    'Review & Submit Changes',
    subtitle: 'You are about to submit changes with the assets listed below. '
            + 'Verify your selections are correct before submitting.',
    confirm:  '✔ Submit',
  },
  edit: {
    icon:     '💾',
    title:    'Review & Save Changes',
    subtitle: 'You are about to save edits to this form. '
            + 'Review the checked assets below and confirm your changes.',
    confirm:  '✔ Confirm & Save',
  },
};

const AssetRow = ({ asset, variant }) => (
  <li
    className={[
      'asset-confirm-list-item',
      variant === 'checked'   ? 'asset-confirm-list-item--checked'   : '',
      variant === 'unchecked' ? 'asset-confirm-list-item--unchecked' : '',
    ].join(' ').trim()}
  >
    <div className="asset-confirm-list-item-grid">
      <span className="asset-confirm-item-name">{asset.name}</span>
      <span className="asset-confirm-item-id">{asset.id}</span>
      <span className="asset-confirm-item-bubble">{asset.calloutNumber}</span>
    </div>
  </li>
);

const SaveConfirmModal = ({
  assetIdToNameRef,
  assetIdToCalloutNumberRef,
  saveVisibleText,
  onCancel,
  preEForm,
  visiblePreEFormMappings = [],
  localPreEFormMappings   = [],
  checkedAssetIds         = [],
  initialCheckedAssetIds  = [],
  onSaveSuccess,
}) => {
  const labelLower = String(saveVisibleText ?? '').toLowerCase();
  const isNewForm  = ['create', 'submit', 'new'].some((kw) => labelLower.includes(kw));
  const copy       = isNewForm ? COPY.new : COPY.edit;

  const checkedSet        = new Set(checkedAssetIds);
  const initialCheckedSet = new Set(initialCheckedAssetIds);

  /**
   * isOwnerEligible — guards the REMOVE group only.
   * An asset can only be "removed" if it was already persisted to this form
   * (i.e. formId matches). This intentionally excludes assets that are being
   * added for the first time — they have no formId yet.
   */
  const isOwnerEligible = (asset) =>
    !!(asset.formId && asset.formId === preEForm.id);

  const enrichAsset = (asset) => {
    const id = String(asset.ipsAssetId ?? asset.assetName ?? '').trim();
    return {
      id,
      name:          extractConductorName(assetIdToNameRef.current.get(id)) || asset.assetName || '',
      calloutNumber: assetIdToCalloutNumberRef.current.get(id) ?? '',
    };
  };

  // 🔧 Helper: derive the canonical string ID from a raw asset object.
  const assetId = (asset) =>
    String(asset.ipsAssetId ?? asset.assetName ?? '').trim();

  // ── Group 1: "Adding" — checked NOW but NOT in initial snapshot ───────────
  // 🔧 For both new AND edit forms we only need the checkedSet / initialCheckedSet
  //    delta.  isOwnerEligible is intentionally NOT applied here — newly added
  //    assets have no formId yet and would be wrongly excluded if it were.
  const newlyCheckedDisplayAssets = visiblePreEFormMappings
    .flatMap((m) => m.assets ?? [])
    .filter((asset) => {
      const id = assetId(asset);
      return checkedSet.has(id) && !initialCheckedSet.has(id); // 🔧 unified for new + edit
    })
    .map(enrichAsset);

  // ── Group 2: "Removing" — was in initial snapshot, now unchecked ─────────
  // isOwnerEligible IS required here: we must confirm the asset was already
  // persisted to this form before treating it as removable.
  const uncheckedDisplayAssets = isNewForm
    ? []
    : localPreEFormMappings
        .flatMap((m) => m.assets ?? [])
        .filter((asset) => {
          const id = assetId(asset);
          return (
            isOwnerEligible(asset) &&  // must be a persisted mapping on this form
            initialCheckedSet.has(id) &&
            !checkedSet.has(id)
          );
        })
        .map(enrichAsset);

  const hasChanges =
    newlyCheckedDisplayAssets.length > 0 || uncheckedDisplayAssets.length > 0;

  const handleConfirm = () => {
    onSaveSuccess?.(checkedAssetIds);
  };

  return (
    <div
      className="asset-confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-confirm-title"
    >
      <div className="asset-confirm-modal">
        <div className="asset-confirm-icon">{copy.icon}</div>
        <h3 id="save-confirm-title" className="asset-confirm-title">{copy.title}</h3>
        <p className="asset-confirm-subtitle">{copy.subtitle}</p>

        {!hasChanges ? (
          <p className="asset-confirm-empty">
            {isNewForm
              ? 'No assets have been selected.'
              : 'No asset changes were made this session.'}
          </p>
        ) : (
          <div className="asset-confirm-list-wrapper">
            <div className="asset-confirm-table-header">
              <span>Asset Name</span>
              <span>IPS Asset ID</span>
              <span>Bubble #</span>
            </div>
            <ul className="asset-confirm-list">
              {newlyCheckedDisplayAssets.map((asset) => (
                <AssetRow key={asset.id} asset={asset} variant="checked" />
              ))}
              {uncheckedDisplayAssets.map((asset) => (
                <AssetRow key={asset.id} asset={asset} variant="unchecked" />
              ))}
            </ul>
            <div className="asset-confirm-legend">
              {newlyCheckedDisplayAssets.length > 0 && (
                <span className="asset-confirm-legend__item asset-confirm-legend__item--checked">
                  ● Adding {newlyCheckedDisplayAssets.length} asset{newlyCheckedDisplayAssets.length !== 1 ? 's' : ''}
                </span>
              )}
              {uncheckedDisplayAssets.length > 0 && (
                <span className="asset-confirm-legend__item asset-confirm-legend__item--unchecked">
                  ● Removing {uncheckedDisplayAssets.length} asset{uncheckedDisplayAssets.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="asset-confirm-actions">
          <Button onClickHandler={handleConfirm} className="btn--primary">
            {copy.confirm}
          </Button>
          <Button onClickHandler={onCancel} className="btn--secondary">
            Go Back
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SaveConfirmModal;
